import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { Download, Printer, Copy, Bike, ArrowLeft, ScanLine, Gift } from "lucide-react";
import api from "../lib/api";

export default function RecruitDriver() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [qr, setQr] = useState("");
  const [promo, setPromo] = useState(null);
  useEffect(() => { api.get("/promo").then((r) => setPromo(r.data)).catch(() => {}); }, []);
  const promoDaysLeft = promo?.free && promo?.free_until ? Math.max(0, Math.ceil((new Date(promo.free_until) - new Date()) / 86400000)) : null;
  const target = `${window.location.origin}/auth?role=driver`;

  useEffect(() => {
    QRCode.toDataURL(target, {
      width: 1024,
      margin: 2,
      errorCorrectionLevel: "H",
      color: { dark: "#1A1A1A", light: "#FFFFFF" },
    }).then(setQr).catch(() => {});
  }, [target]);

  const download = () => {
    const a = document.createElement("a");
    a.href = qr;
    a.download = "bentskin-go-qr-chauffeur.png";
    a.click();
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(target);
    toast.success(t("recruit.linkCopied"));
  };

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <div className="print:hidden"><AppHeader variant="landing" /></div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-20">
        <button
          data-testid="recruit-back"
          onClick={() => navigate("/")}
          className="print:hidden inline-flex items-center gap-2 text-[#75807A] hover:text-[#D94F2B] text-sm font-medium mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {t("recruit.back")}
        </button>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="print:hidden"
          >
            <p className="text-[#D94F2B] text-sm font-semibold uppercase tracking-[0.1em]">{t("recruit.kicker")}</p>
            <h1 className="mt-3 font-head font-black text-4xl sm:text-5xl tracking-tight text-[#1A1A1A] leading-[1.05]">
              {t("recruit.title")}
            </h1>
            <p className="mt-5 text-[#3B423E] text-lg leading-relaxed max-w-md">{t("recruit.subtitle")}</p>
            {false && promoDaysLeft != null && (
              <div data-testid="recruit-promo-countdown" className="mt-5 rounded-2xl p-4 bg-gradient-to-br from-[#16a34a] to-[#0f8a3c] text-white max-w-md shadow-[0_8px_30px_rgba(22,163,74,0.25)]">
                <div className="flex items-center gap-2 font-head font-bold text-lg"><Gift className="w-5 h-5" /> Lancement gratuit</div>
                <p className="mt-1 text-sm text-white/90"><b>0 % de commission</b> — vous gardez 100 % de vos gains. Dépôt de garantie offert.</p>
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-bold">⏳ Plus que {promoDaysLeft} jour{promoDaysLeft > 1 ? "s" : ""} pour vous inscrire gratuitement</div>
              </div>
            )}
            <p className="mt-4 text-[#75807A] text-sm">{t("recruit.stickerNote")}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="recruit-download"
                onClick={download}
                disabled={!qr}
                className="h-14 px-6 rounded-2xl bg-[#F26419] hover:bg-[#D85312] disabled:opacity-50 text-white font-bold inline-flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5"
              >
                <Download className="w-5 h-5" /> {t("recruit.download")}
              </button>
              <button
                data-testid="recruit-print"
                onClick={() => window.print()}
                className="h-14 px-6 rounded-2xl bg-white border border-[#E2DFD8] hover:border-[#D94F2B] text-[#1A1A1A] font-bold inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Printer className="w-5 h-5" /> {t("recruit.print")}
              </button>
            </div>
            <button
              data-testid="recruit-copy"
              onClick={copyLink}
              className="mt-4 inline-flex items-center gap-2 text-[#75807A] hover:text-[#D94F2B] text-sm font-medium transition-colors"
            >
              <Copy className="w-4 h-4" /> {t("recruit.copyLink")}
            </button>
          </motion.div>

          {/* Sticker / QR card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.1 }}
            className="flex justify-center"
          >
            <div
              data-testid="recruit-qr-card"
              className="bg-white rounded-[2rem] border border-[#E2DFD8] shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-8 w-full max-w-sm text-center print:shadow-none print:border-2"
            >
              <div className="flex items-center justify-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F26419] to-[#D94F2B] flex items-center justify-center">
                  <Bike className="w-5 h-5 text-white" strokeWidth={2.4} />
                </span>
                <span className="font-head font-extrabold text-xl tracking-tight text-[#1A1A1A]">
                  Mboa<span className="text-[#F26419]"> Go</span>
                </span>
              </div>

              <div className="mt-6 rounded-2xl bg-[#FDF8F0] p-4 inline-block">
                {qr ? (
                  <img data-testid="recruit-qr-img" src={qr} alt="QR Code" className="w-56 h-56 sm:w-64 sm:h-64" />
                ) : (
                  <div className="w-56 h-56 sm:w-64 sm:h-64 animate-pulse bg-[#E2DFD8] rounded-xl" />
                )}
              </div>

              <div className="mt-5 inline-flex items-center gap-2 text-[#D94F2B] font-bold">
                <ScanLine className="w-5 h-5" /> {t("recruit.scanHint")}
              </div>
              <p className="mt-2 text-[#75807A] text-sm">{t("recruit.becomeDriver")} · FCFA</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
